import {
  SalesTaxFilingFrequencyEnum,
  SalesTaxFilingStatusEnum,
} from '@prisma/client';

import { TIndirectTaxTransactionStatus } from '../transactions/types';

// =============================================================================
// Enums
// =============================================================================

export enum IndirectTaxFilingV2JurisdictionType {
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
  PROVINCE = 'PROVINCE',
  STATE_OR_PROVINCE = 'STATE_OR_PROVINCE',
  MUNICIPALITY = 'MUNICIPALITY',
  FEDERAL = 'FEDERAL',
  TRADEBLOC = 'TRADEBLOC',
  LOCAL = 'LOCAL',
  COUNTY = 'COUNTY',
  CITY = 'CITY',
  DISTRICT = 'DISTRICT',
  RESERVATION = 'RESERVATION',
  TERRITORY = 'TERRITORY',
}

export enum IndirectTaxFilingV2DocumentType {
  RETURN = 'RETURN',
  FILING_CONFIRMATION = 'FILING_CONFIRMATION',
  PAYMENT_CONFIRMATION = 'PAYMENT_CONFIRMATION',
}

export enum IndirectTaxFilingV2ExportDeliveryType {
  EMAIL = 'EMAIL',
}

export enum IndirectTaxFilingV2ExportScenario {
  ACTUAL = 'ACTUAL',
  ADJUSTED = 'ADJUSTED',
  EXPECTED = 'EXPECTED',
}

export enum IndirectTaxFilingV2ExportMode {
  RAW = 'RAW',
  JURISDICTION_SPECIFIC = 'JURISDICTION_SPECIFIC',
}

// Classifies a VAT return box: AGGREGATE boxes sum directly from invoice data
// (gross sales, taxable sales, intra-community sales, …) and render under
// "Breakdown". FORMULA boxes are derived from other boxes (estimated tax,
// prepayments, rounding) and render under "Adjustments".
export enum IndirectTaxFilingV2ContributionBoxType {
  AGGREGATE = 'AGGREGATE',
  FORMULA = 'FORMULA',
}

// Values supported by the V2 list endpoint's `extend` query parameter.
export enum IndirectTaxFilingV2ListExtend {
  FILING_CONTRIBUTION = 'filing_contribution',
}

export { SalesTaxFilingFrequencyEnum, SalesTaxFilingStatusEnum };

// =============================================================================
// Domain shapes (match logical-backend types/filing-v2.types.ts)
// =============================================================================

// Money fields — all flat on IIndirectTaxFilingV2 below. Kept as a separate
// interface only so consumers can type-narrow to just the money surface.
//
// Scaling contract: STA2's DAO divides bigint picounits by 1e12 before JSON
// emission, so every field here is in MAJOR currency units (dollars, euros,
// pounds). FE must NOT divide again.
export interface IIndirectTaxFilingV2Amounts {
  taxableAmount: number | null;
  estimatedTaxableAmount: number | null;
  nonTaxableAmount: number | null;
  estimatedNonTaxableAmount: number | null;
  exemptAmount: number | null;
  estimatedExemptAmount: number | null;
  marketplaceAmount: number | null;
  estimatedMarketplaceAmount: number | null;
  estimatedTaxCollected: number | null;
  taxLiability: number | null;
  taxPaid: number | null;
  taxRemitted: number | null;
  discount: number | null;
  vendorDiscount: number | null;
  rounding: number | null;
  penalty: number | null;
  interest: number | null;
  adjustment: number | null;
  prepayment: number | null;
  priorPayment: number | null;
  retailDeliveryFee: number | null;
  jurisdictionIssuedCredit: number | null;
  marketplacePayment: number | null;
  filingTransactionsCount: number | null;
}

export interface IIndirectTaxFilingV2ServiceRequestTask {
  id: string;
  legacyServiceRequestTaskTaskId: string;
  title: string;
  pendingOn: string | null;
  status: string;
  legacyServiceRequestTaskOrderNumber: number | null;
}

export interface IIndirectTaxFilingV2ServiceRequestTaskGroup {
  id: string;
  title: string;
  status: string;
  legacyServiceRequestTaskGroupOrderNumber: number | null;
  tasks: IIndirectTaxFilingV2ServiceRequestTask[];
}

export interface IIndirectTaxFilingV2ServiceRequestAssociation {
  id: string;
  status: string;
  chatRoomId: string | null;
  workflowExecutionId: string | null;
  taskGroups: IIndirectTaxFilingV2ServiceRequestTaskGroup[];
}

export interface IIndirectTaxFilingV2AssignedUserAssociation {
  id: string;
  name: string;
  email: string;
}

export interface IIndirectTaxFilingV2FiledByAgentAssociation {
  id: string;
  name: string;
}

export interface IIndirectTaxFilingV2Contribution {
  id: string;
  filingId: string;
  filingContributionContentId: string;
  // Populated only on OSS/IOSS rows. Identifies the EU member state the
  // contribution is filed *through* when a single return covers multiple
  // jurisdictions.
  filingThroughJurisdiction: string | null;
  value: number; // minor currency units (e.g. cents, pence)
  // Operator override for `value`, in the same minor currency units. Null when
  // no override has been set. When present, STA2 uses it in place of the
  // computed `value` and recomputes dependent FORMULA boxes. The return summary
  // prefers `adjustedValue ?? value`.
  adjustedValue: number | null;
  valueType: string;
  // Resolved filing rate for this box: appliedRate is percentage x 10000
  // (190000 = 19%); appliedRateType names the tier (STANDARD, REDUCED_1..3,
  // REDUCED, ZERO_RATED, ANY). Both null for ANY / FORMULA boxes. A bare
  // REDUCED (OSS) box fans into one contribution per distinct applied rate, so
  // several rows can share a boxId/boxName and differ only by appliedRate.
  appliedRate: number | null;
  appliedRateType: string | null;
  boxId: string;
  boxName: string;
  boxOrder: number;
  boxType: IndirectTaxFilingV2ContributionBoxType;
  summaryBucket: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IUseIndirectTaxFilingsV2Options {
  keepPreviousData?: boolean;
}

export interface IIndirectTaxFilingV2 extends IIndirectTaxFilingV2Amounts {
  reportingCurrency?: string | null;
  // Older filings returned by this endpoint identify jurisdiction with ISO codes.
  country?: string | null;
  state?: string | null;
  id: string;
  filingId: string;
  corporationId: number;
  registrationTaxTypeId: string;
  jurisdictionId: string;
  jurisdictionName: string;
  jurisdictionType: string;
  formTitle: string;
  description: string | null;
  filingFrequency: SalesTaxFilingFrequencyEnum;
  status: SalesTaxFilingStatusEnum;
  taxPeriodStartDate: string;
  taxPeriodEndDate: string;
  reportingPeriodStartDate: string;
  reportingPeriodEndDate: string;
  filingDueDate: string;
  dueDate: string | null;
  filedAt: string | null;
  filingLockedAt: string | null;
  filingConfirmationNumber: string | null;
  paymentConfirmationNumber: string | null;
  // Present only when the list endpoint is called with
  // extend=filing_contribution (see IIndirectTaxFilingsV2ListParams.extend).
  contributions?: IIndirectTaxFilingV2Contribution[];
}

export interface IIndirectTaxFilingV2WithAssociations
  extends IIndirectTaxFilingV2 {
  serviceRequest?: IIndirectTaxFilingV2ServiceRequestAssociation | null;
  assignedUser?: IIndirectTaxFilingV2AssignedUserAssociation | null;
  filedByAgent?: IIndirectTaxFilingV2FiledByAgentAssociation | null;
}

export interface IIndirectTaxFilingV2Document {
  id: string;
  filingId: string;
  fileId: string;
  documentType: IndirectTaxFilingV2DocumentType;
  createdAt: string;
}

export interface IIndirectTaxFilingV2DocumentWithFile
  extends IIndirectTaxFilingV2Document {
  fileName: string;
  signedUrl: string;
  createdAtEpochMicros: number;
}

export interface IIndirectTaxFilingV2TransactionBreakdown {
  jurisdictionName: string;
  jurisdictionType: string;
  taxAmount: number;
  rate: number;
  invoiceCount: number;
  taxableAmount: number;
  nonTaxableAmount: number;
}

export interface IIndirectTaxFilingV2TransactionSummary {
  filingId: string;
  formTitle: string;
  filingDueDate: string;
  taxPeriodStartDate: string;
  taxPeriodEndDate: string;
  status: string;
  jurisdictionName: string | null;
  jurisdictionType: string | null;
  registrationTaxTypeId: string | null;
  totalTaxableAmount: number;
  totalNonTaxableAmount: number;
  totalTaxAmount: number;
  invoiceCount: number;
  jurisdictionBreakdown: IIndirectTaxFilingV2TransactionBreakdown[];
}

export interface IIndirectTaxFilingV2FrequencyChangeEntry {
  id: string;
  formTitle: string;
  registrationTaxTypeId: string;
  jurisdictionId: string;
  jurisdictionName: string;
  jurisdictionType: string;
  filingDueDate: string;
  taxPeriodStartDate: string;
  taxPeriodEndDate: string;
  reportingPeriodStartDate: string;
  reportingPeriodEndDate: string;
  status: string;
  filingFrequency: string;
}

export interface IIndirectTaxFilingV2FrequencyChangeLog {
  corporationId: string;
  registrationTaxTypeId: string;
  jurisdictionName: string | null;
  jurisdictionType: string | null;
  previousFrequency: string;
  newFrequency: string;
  changedAt: number;
  createdAt: number;
  addedEntries: IIndirectTaxFilingV2FrequencyChangeEntry[];
  removedEntries: IIndirectTaxFilingV2FrequencyChangeEntry[];
  finalEntries: IIndirectTaxFilingV2FrequencyChangeEntry[];
}

export interface IIndirectTaxFilingV2CoverageGap {
  taxPeriodStartDate: string;
  taxPeriodEndDate: string;
  previouslyCoveredBy: string;
  reason: string;
}

export interface IIndirectTaxFilingV2CalendarPreview {
  existingFilings: IIndirectTaxFilingV2[];
  proposedFilings: IIndirectTaxFilingV2[];
  filingsToDelete: IIndirectTaxFilingV2[];
  finalCalendar: IIndirectTaxFilingV2[];
  hasFiledConflict: boolean;
  conflictingFilings?: IIndirectTaxFilingV2[];
  hasCoverageGaps: boolean;
  coverageGaps?: IIndirectTaxFilingV2CoverageGap[];
  validationErrors?: string[];
  canGenerate: boolean;
}

// =============================================================================
// Include flags (shared by list + get)
// =============================================================================

export interface IIndirectTaxFilingV2IncludeFlags {
  includeServiceRequest?: boolean;
  includeAssignedUser?: boolean;
  includeFiledByAgent?: boolean;
}

// =============================================================================
// Request params
// =============================================================================

export interface IIndirectTaxFilingsV2ListParams
  extends IIndirectTaxFilingV2IncludeFlags {
  corporationId: number;
  registrationTaxTypeId?: string;
  jurisdictionIds?: string[];
  filingStatuses?: SalesTaxFilingStatusEnum[];
  filingFrequencies?: SalesTaxFilingFrequencyEnum[];
  fromDueDate?: string;
  toDueDate?: string;
  cursor?: string;
  limit?: number;
  // Opt-in list expansions. Each value populates an otherwise-absent field on
  // each returned filing (e.g. FILING_CONTRIBUTION populates `contributions`).
  extend?: ReadonlyArray<IndirectTaxFilingV2ListExtend>;
}

export interface IIndirectTaxFilingV2ContributionsParams {
  corporationId: number;
}

export interface IIndirectTaxFilingV2UpdateContributionsParams {
  corporationId: number;
  // Each item overrides one contribution's value. `adjustedValue` is in minor
  // currency units (cents, pence); pass null to clear a prior override. Must be
  // non-empty (the backend rejects an empty list).
  contributions: { id: string; adjustedValue: number | null }[];
}

export interface IIndirectTaxFilingV2GetParams
  extends IIndirectTaxFilingV2IncludeFlags {
  corporationId: number;
}

export interface IIndirectTaxFilingV2UpdateParams {
  corporationId: number;
  taxableAmount?: number;
  nonTaxableAmount?: number;
  exemptAmount?: number;
  marketplaceAmount?: number;
  taxCollected?: number;
  taxLiability?: number;
  taxPaid?: number;
  taxRemitted?: number;
  discount?: number;
  rounding?: number;
  penalty?: number;
  interest?: number;
  adjustment?: number;
  prepayment?: number;
  priorPayment?: number;
  retailDeliveryFee?: number;
  jurisdictionIssuedCredit?: number;
  marketplacePayment?: number;
  vendorDiscount?: number;
  filingConfirmationNumber?: string;
  paymentConfirmationNumber?: string;
  filedAt?: string;
}

export interface IIndirectTaxFilingV2CreateDocumentParams {
  corporationId: number;
  fileId: string;
  documentType: IndirectTaxFilingV2DocumentType;
  fileName: string;
  fileUrl: string;
}

export interface IIndirectTaxFilingV2CalendarParams {
  corporationId: number;
  registrationTaxTypeId: string;
  filingFrequencies?: SalesTaxFilingFrequencyEnum[];
  taxPeriodStartMonth: string;
  taxPeriodEndMonth?: string;
}

export interface IIndirectTaxFilingV2BulkDeleteParams {
  corporationId: number;
  registrationTaxTypeId: string;
  statuses: SalesTaxFilingStatusEnum[];
  dueAfter?: string;
}

export interface IIndirectTaxFilingV2BulkDeleteByIdsParams {
  corporationId: number;
  filingIds: string[];
}

export interface IIndirectTaxFilingV2BulkLockParams {
  corporationId: number;
  filingIds: string[];
}

export interface IIndirectTaxFilingV2BulkLockResponse {
  lockedIds: string[];
  failedIds: string[];
  totalLocked: number;
  message: string;
}

export interface IIndirectTaxFilingV2BulkReviewParams {
  corporationId: number;
  filingIds: string[];
}

export interface IIndirectTaxFilingV2BulkReviewResponse {
  reviewedIds: string[];
  skippedIds: string[];
  totalReviewed: number;
  message: string;
}

export interface IIndirectTaxFilingV2ExportParams {
  corporationId: number;
  filingId: string;
  deliveryType: IndirectTaxFilingV2ExportDeliveryType;
  deliveryRecipients: string[];
  exportMode?: IndirectTaxFilingV2ExportMode;
}

export interface IIndirectTaxFilingV2ExportForDateRangeParams {
  corporationId: number;
  dueDateStart?: string;
  dueDateEnd?: string;
  deliveryType: IndirectTaxFilingV2ExportDeliveryType;
  deliveryRecipients: string[];
}

export interface IIndirectTaxFilingV2FrequencyChangeLogsParams {
  corporationId: number;
  registrationTaxTypeId: string;
  cursor?: string;
  limit?: number;
}

// =============================================================================
// Response envelopes
// =============================================================================

export interface IIndirectTaxFilingV2ListResponse {
  filings: IIndirectTaxFilingV2WithAssociations[];
  count: number;
  nextCursor: string | null;
}

export interface IIndirectTaxFilingV2ContributionsResponse {
  contributions: IIndirectTaxFilingV2Contribution[];
  count: number;
}

export interface IIndirectTaxFilingV2GetResponse {
  filing: IIndirectTaxFilingV2WithAssociations;
}

export interface IIndirectTaxFilingV2UpdateResponse {
  filing: IIndirectTaxFilingV2;
  message: string;
}

export interface IIndirectTaxFilingV2SubmitResponse {
  message: string;
}

export interface IIndirectTaxFilingV2ApprovalResponse {
  message: string;
}

export interface IIndirectTaxFilingV2AssignResponse {
  filing: IIndirectTaxFilingV2WithAssociations;
  message: string;
}

export interface IIndirectTaxFilingV2DocumentResponse {
  document: IIndirectTaxFilingV2Document;
  message: string;
}

export interface IIndirectTaxFilingV2DocumentListResponse {
  documents: IIndirectTaxFilingV2Document[];
  count: number;
}

export interface IIndirectTaxFilingV2PrepaymentListResponse {
  prepayments: IIndirectTaxFilingV2[];
  count: number;
  globalizationPending?: boolean;
}

export interface IIndirectTaxFilingV2TransactionSummaryResponse {
  summaries: IIndirectTaxFilingV2TransactionSummary[];
  count: number;
}

export interface IIndirectTaxFilingV2FrequencyChangeLogListResponse {
  changes: IIndirectTaxFilingV2FrequencyChangeLog[];
  count: number;
  nextCursor: string | null;
  globalizationPending?: boolean;
}

export interface IIndirectTaxFilingV2CalendarPreviewResponse {
  preview: IIndirectTaxFilingV2CalendarPreview;
  message: string;
}

export interface IIndirectTaxFilingV2CalendarGenerateResponse {
  createdFilings: IIndirectTaxFilingV2[];
  deletedFilings: IIndirectTaxFilingV2[];
  totalCreated: number;
  totalDeleted: number;
  message: string;
}

export interface IIndirectTaxFilingV2BulkDeleteResponse {
  deletedIds: string[];
  totalDeleted: number;
  message: string;
}

export interface IIndirectTaxFilingV2ExportJobResponse {
  jobId: string;
  message: string;
}

/** Row breakdown from GET liability-report (US/Canada). */
export interface IIndirectTaxFilingV2LiabilityReportBreakdown {
  transactionsCount: number;
  estimatedTaxableAmount: number;
  estimatedNonTaxableAmount: number;
  estimatedExemptAmount: number;
  estimatedTaxCollected: number;
}

/** One filing row from GET liability-report. */
export interface IIndirectTaxFilingV2LiabilityReportFiling {
  filingId: string;
  formTitle: string;
  jurisdiction: string;
  taxPeriodStartDate: string;
  taxPeriodEndDate: string;
  dueDate: string;
  filingStatus: string;
  breakdown: IIndirectTaxFilingV2LiabilityReportBreakdown | null;
  contributions: IIndirectTaxFilingV2Contribution[] | null;
}

export interface IIndirectTaxFilingV2GetLiabilityReportResponse {
  filings: IIndirectTaxFilingV2LiabilityReportFiling[];
}

export interface IIndirectTaxFilingV2LiabilityReportParams {
  corporationId: number;
  filingIds: string[];
}

/** POST …/liability-report/export — filing IDs plus export delivery emails. */
export interface IIndirectTaxFilingV2ExportLiabilityReportParams
  extends IIndirectTaxFilingV2LiabilityReportParams {
  deliveryRecipients: string[];
  /** When true, the exported report includes per-invoice (line-item) detail. */
  includeInvoiceDetail?: boolean;
}

export interface IIndirectTaxFilingV2SendApprovalMailParams
  extends IIndirectTaxFilingV2LiabilityReportParams {
  recipients: string[];
}

export interface IIndirectTaxFilingV2SendApprovalMailResponse {
  queuedFilingIds: string[];
  skippedFilingIds: string[];
  recipientCount: number;
  message: string;
}

// =============================================================================
// Filings-status endpoint
// =============================================================================
//
// Backs POST /api/v1/new-sales-tax/filings-v2/status. Frontend pre-splits
// filing IDs by region (US/CA → breakdown; UK/EU → contribution) and the
// response keeps that split — no discriminated union, no per-filing routing
// on the server. Either subkey is null when its input list is absent or
// empty.

export interface IIndirectTaxFilingV2StatusParams {
  corporationId: number;
  breakdownFilingIds?: string[];
  contributionFilingIds?: string[];
}

/**
 * Per-filing breakdown rollup row. Counts come from invoice-column GROUP BY
 * on tax_breakdown_status; total_invoices_in_range is the sum of the five
 * status counters. total_invoices_locked is always 0 from this endpoint —
 * it's not derivable from the breakdown grouping. Use the legacy
 * /filings/progress path if you need the locked count.
 */
export interface IIndirectTaxFilingV2StatusBreakdownRow {
  filingId: string;
  filingName: string;
  filingFrequency: string;
  status: string;
  filingDueDate: string;
  state: string;
  country: string;
  taxPeriodStartDate: string;
  taxPeriodEndDate: string;
  totalInvoicesInRange: number;
  totalInvoicesLocked: number;
  totalBreakdownsNotStarted: number;
  totalBreakdownsQueued: number;
  totalBreakdownsInProgress: number;
  totalBreakdownsCompleted: number;
  totalBreakdownsFailed: number;
}

/**
 * Latest FILING_CONTRIBUTION SYNC job row per filing. entityId === filing.id.
 * Per Phase 1 of this PR chain, the attach-side job is no longer surfaced
 * separately — sync status alone tells the caller whether contribution
 * computation finished.
 */
export interface IIndirectTaxFilingV2StatusContributionJob {
  id: string;
  entity: string;
  entityId: string;
  operation: string;
  status: string;
  totalRows: number;
  processedRows: number;
  successCount: number;
  failureCount: number;
  failureReasons: string[];
  submittedAt: number;
  completedAt: number | null;
}

export interface IIndirectTaxFilingV2StatusResponse {
  breakdown: { filings: IIndirectTaxFilingV2StatusBreakdownRow[] } | null;
  contribution: { jobs: IIndirectTaxFilingV2StatusContributionJob[] } | null;
}

/**
 * The latest tax-breakdown calculation error for an invoice whose breakdown
 * FAILED, with the invoice context nested under `invoiceDetails`. `createdAt`
 * is unix microseconds.
 */
export interface IIndirectTaxFilingV2TaxBreakdownError {
  jobErrorId: string;
  jobId: string;
  rowNumber: number;
  errorMessage: string;
  createdAt: number;
  invoiceDetails: {
    id: string;
    transactedAt?: string;
    reportingDateOverride?: string;
    invoiceCurrency?: string;
    sourcePlatform?: string;
    sourcePlatformId?: string;
    taxBreakdownStatus: string;
  };
}

export interface IIndirectTaxFilingV2BreakdownErrorsParams {
  corporationId: number;
  filingId: string;
}

export interface IIndirectTaxFilingV2BreakdownErrorsResponse {
  taxBreakdownErrors: IIndirectTaxFilingV2TaxBreakdownError[];
  total: number;
}

// =============================================================================
// Trigger endpoints — re-run the underlying jobs for a list of filings
// =============================================================================
//
// Both wrap logical-backend pass-throughs to sales-tax-api-2:
//   - Breakdown: POST /new-sales-tax/corporations/:id/tax-breakdowns/generate
//     (forwarded as POST /tax-breakdowns/trigger with filing_ids body)
//   - Contribution sync: POST /new-sales-tax/filings-v2/trigger-contribution-sync
//     (admin-only, agent-app role; forwarded as
//     POST /internal/filings/contributions/trigger)

export interface IIndirectTaxFilingV2TriggerBreakdownParams {
  corporationId: number;
  filingIds: string[];
}

export interface IIndirectTaxFilingV2TriggerBreakdownResponse {
  jobId?: string;
  message: string;
}

// =============================================================================
// Admin tax-breakdown tools — the full upstream filter surface
// =============================================================================
//
// The narrower helpers above cover the enterprise-app flows (one filing, or a
// filing due-date range). These two cover everything sales-tax-api-2 accepts
// and back the admin indirect-tax tools.
//
// Upstream requires exactly ONE filter group per request and returns a 400
// naming the offending combination, so these types keep every group optional
// rather than modelling the exclusivity as a union — the UI picks a group and
// the server stays the single source of truth.

/** Filter groups: invoice | filing ids | filing due dates | transaction dates. */
export interface IIndirectTaxBreakdownTriggerParams {
  corporationId: number;

  invoiceId?: string;

  filingId?: string;
  /** Max 100 upstream. */
  filingIds?: string[];

  dueDateStart?: string;
  dueDateEnd?: string;

  /** ISO 3166 alpha-2. Required with state and both transaction dates. */
  country?: string;
  state?: string;
  transactionStartDate?: string;
  transactionEndDate?: string;

  /** Recomputes breakdowns for invoices already marked COMPLETED. */
  forceRegenerate?: boolean;
}

/** Filter groups: filing ids | filing due dates | state + transaction dates. */
export interface IIndirectTaxBreakdownExportParams {
  corporationId: number;

  filingId?: string;
  /** Max 100 upstream. */
  filingIds?: string[];

  filingDueDateStart?: string;
  filingDueDateEnd?: string;

  /** Required with both transaction dates. Note: no country on export. */
  state?: string;
  transactionStartDate?: string;
  transactionEndDate?: string;

  scenarios?: IndirectTaxFilingV2ExportScenario[];
  /** Defaults to all statuses upstream when omitted. */
  invoiceStatuses?: TIndirectTaxTransactionStatus[];
  /** Rejected upstream alongside exportMode JURISDICTION_SPECIFIC. */
  includeLineItems?: boolean;
  exportMode?: IndirectTaxFilingV2ExportMode;

  /**
   * Non-empty mails a signed link to each address. Empty or omitted sends no
   * email — fetch the CSV from the job's export_file_id instead.
   */
  deliveryRecipients?: string[];
}

export interface IIndirectTaxBreakdownJobResponse {
  jobId?: string;
  message: string;
}

export interface IIndirectTaxFilingV2TriggerContributionSyncParams {
  corporationId: number;
  filingIds: string[];
}

export interface IIndirectTaxFilingV2TriggerContributionSyncResult {
  filingId: string;
  attachJobId: string | null;
  contributionJobId: string | null;
  skipped: boolean;
  skipReason: string | null;
  success: boolean;
  error: string | null;
}

export interface IIndirectTaxFilingV2TriggerContributionSyncResponse {
  results: IIndirectTaxFilingV2TriggerContributionSyncResult[];
  successCount: number;
  failureCount: number;
  skippedCount: number;
}

// Aggregate-sync trigger — fire-and-forget re-run of per-filing aggregate
// (filing-numbers) jobs for US/CA filings. The endpoint is non-idempotent
// (each call queues fresh jobs) and intentionally does not return a job_id;
// only the acknowledgment message is surfaced. Wraps logical-backend
// POST /new-sales-tax/corporations/:id/filing-aggregates/sync.
export interface IIndirectTaxFilingV2TriggerAggregateSyncParams {
  corporationId: number;
  filingIds: string[];
}

export interface IIndirectTaxFilingV2TriggerAggregateSyncResponse {
  message: string;
}

// =============================================================================
// Mutation result envelope (standard frontend shape)
// =============================================================================

export interface IIndirectTaxFilingV2MutationResult<TData> {
  success: boolean;
  data?: TData;
  error?: string;
}

// =============================================================================
// Filings aggregate endpoint
// =============================================================================
//
// Backs POST /api/v1/new-sales-tax/filings-v2/aggregate. Returns bucketed
// summary totals (taxable_sales / vat_position / etc.) across every filing
// matching the filter set — not just the visible page, so the dashboard
// summary cards stay correct under pagination.
//
// Strictly single-region by design: ALL_REGIONS is intentionally absent from
// the params region union so it can't even be constructed without a cast.
// Logical backend rejects with 400 if it does sneak through; STA2 rejects
// again as defense-in-depth.

export type IIndirectTaxFilingV2AggregateRegion =
  | 'UNITED_STATES'
  | 'EUROPEAN_UNION'
  | 'UNITED_KINGDOM'
  | 'CANADA';

export interface IIndirectTaxFilingV2AggregateParams {
  corporationId: number;
  region: IIndirectTaxFilingV2AggregateRegion;
  registrationTaxTypeId?: string;
  jurisdictionIds?: string[];
  filingStatuses?: SalesTaxFilingStatusEnum[];
  filingFrequencies?: SalesTaxFilingFrequencyEnum[];
  fromDueDate?: string;
  toDueDate?: string;
}

/**
 * Per-bucket totals dictionary. Region-shape: only the buckets the active
 * region's SUMMARY_CONFIGS exposes are populated; the rest stay undefined
 * so consumers can distinguish "not in this region's config" from "zero
 * this period". Major-unit decimals (the backend handles picounit / minor
 * scaling before emission).
 */
export interface IIndirectTaxFilingV2AggregateTotals {
  // Common to US + Canada (flat-field backed).
  taxableSales?: number;
  nonTaxableSales?: number;
  exemptSales?: number;
  taxDue?: number;
  totalSales?: number;

  // EU + UK only (content summary_bucket backed).
  taxablePurchases?: number;
  intraCommunitySales?: number;
  intraCommunityPurchases?: number;
  vatPosition?: number;
}

export interface IIndirectTaxFilingV2AggregateResponse {
  region: IIndirectTaxFilingV2AggregateRegion;
  currency: string;
  totals: IIndirectTaxFilingV2AggregateTotals;
}

export enum IIndirectTaxFilingServiceRequestBackfillOutcome {
  CREATED = 'CREATED',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  INELIGIBLE_STATUS = 'INELIGIBLE_STATUS',
  MANAGED_FILING_DISABLED = 'MANAGED_FILING_DISABLED',
  CORPORATION_MISMATCH = 'CORPORATION_MISMATCH',
  FILING_NOT_FOUND = 'FILING_NOT_FOUND',
  FAILED = 'FAILED',
}

export interface IIndirectTaxFilingServiceRequestBackfillResult {
  filingId: string;
  outcome: IIndirectTaxFilingServiceRequestBackfillOutcome;
  serviceRequestId: string | null;
  state: string | null;
  status: string | null;
  filingDueDate: string | null;
  detail: string | null;
}

export interface IIndirectTaxFilingServiceRequestBackfillParams {
  corporationId: number;
  filingIds: string[];
}

export interface IIndirectTaxFilingServiceRequestBackfillResponse {
  results: IIndirectTaxFilingServiceRequestBackfillResult[];
  message: string;
}

export interface IIndirectTaxFilingMissingServiceRequestItem {
  filingId: string;
  corporationId: number | null;
  companyLegalName: string | null;
  state: string | null;
  jurisdictionName: string | null;
  status: string;
  filingDueDate: string;
  filingFrequency: string;
}

export interface IIndirectTaxFilingsMissingServiceRequestParams {
  corporationId?: number;
  statuses?: string[];
  dueDateFrom?: string;
  limit?: number;
}
